import { useEffect, useState } from 'react';
import { usePageMeta } from '../lib/usePageMeta';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { defaultStay, stayQuery, useStayParams } from '../lib/stayParams';
import { useSettings } from '../lib/useSettings';
import { AvailabilityBadge } from '../components/RoomCard';
import { PriceBreakdown } from '../components/PriceBreakdown';
import { Notice, Section } from '../components/Section';
import { StayPicker } from '../components/StayPicker';
import { money } from '../lib/format';

export function RoomDetailPage() {
  usePageMeta('Room details', 'Room details, amenities, and a live price for your dates.');
  const { slug = '' } = useParams();
  const navigate = useNavigate();
  const { data: settings } = useSettings();
  const { stay, update, hasDates } = useStayParams();
  const [activeImage, setActiveImage] = useState(0);

  useEffect(() => {
    if (!hasDates && settings) {
      const preset = defaultStay(settings);
      if (preset) update(preset);
    }
  }, [hasDates, settings, update]);

  const window = hasDates ? { checkIn: stay.checkIn!, checkOut: stay.checkOut! } : null;
  const room = useQuery({
    queryKey: ['room', slug, window?.checkIn, window?.checkOut],
    queryFn: ({ signal }) => api.rooms.detail(slug, window, signal),
    retry: false,
  });
  const quote = useQuery({
    queryKey: ['quote', slug, stay.checkIn, stay.checkOut, stay.adults, stay.children],
    queryFn: ({ signal }) =>
      api.quote(
        {
          slug,
          checkIn: stay.checkIn!,
          checkOut: stay.checkOut!,
          adults: stay.adults,
          children: stay.children,
        },
        signal,
      ),
    enabled: hasDates,
    staleTime: 10_000,
    refetchInterval: 30_000,
  });

  if (room.isLoading)
    return (
      <Section>
        <p className="text-ink-400">Loading room…</p>
      </Section>
    );
  if (room.error || !room.data) {
    return (
      <Section narrow>
        <Notice tone="danger">That room is not available.</Notice>
        <Link to="/rooms" className="btn-outline mt-6">
          Back to rooms
        </Link>
      </Section>
    );
  }

  const r = room.data;
  const images = r.images.length > 0 ? r.images : r.primaryImage ? [r.primaryImage] : [];
  const q = quote.data;
  const canReserve = Boolean(q && q.valid && !q.soldOut);

  return (
    <Section>
      <Link to={`/rooms${stayQuery(stay)}`} className="text-sm text-ink-400 hover:text-pink-300">
        ← All rooms
      </Link>
      {/* min-w-0 on the columns: the thumbnail strip must scroll inside its column, not widen the page. */}
      <div className="mt-4 grid gap-10 lg:grid-cols-[1fr_380px] [&>*]:min-w-0">
        <div>
          <div className="mb-3 flex items-center gap-3">
            <AvailabilityBadge soldOut={r.soldOut} remaining={r.remaining} />
            <span className="text-meta text-sm">{r.code}</span>
          </div>
          <h1 className="text-5xl">{r.name}</h1>
          {r.shortDescription && <p className="mt-2 text-lg text-ink-400">{r.shortDescription}</p>}

          {images.length > 0 && (
            <div className="mt-6">
              <div className="aspect-[16/10] overflow-hidden  bg-night-800">
                <img
                  src={images[activeImage]?.url}
                  alt={images[activeImage]?.altText ?? r.name}
                  className="h-full w-full object-cover"
                />
              </div>
              {images.length > 1 && (
                <div className="mt-3 flex gap-2 overflow-x-auto">
                  {images.map((img, i) => (
                    <button
                      key={img.id}
                      type="button"
                      onClick={() => setActiveImage(i)}
                      className={`h-16 w-24 shrink-0 overflow-hidden  border ${i === activeImage ? 'border-pink-400' : 'border-transparent'}`}
                      aria-label={`Photo ${i + 1}`}
                    >
                      <img src={img.url} alt="" className="h-full w-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          <ul className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-sm text-ink-200">
            {r.bedConfiguration && <li>{r.bedConfiguration}</li>}
            {r.sizeSqft && <li>{r.sizeSqft} sq ft</li>}
            {r.view && <li>{r.view} view</li>}
            {r.floor && <li>Floor: {r.floor}</li>}
            {r.isAccessible && <li>Accessible</li>}
            <li>{r.isSmoking ? 'Smoking' : 'Non-smoking'}</li>
          </ul>
          {r.description && (
            <p className="mt-6 whitespace-pre-line text-ink-200">{r.description}</p>
          )}

          {r.amenities.length > 0 && (
            <div className="mt-8">
              <h2 className="text-2xl">Amenities</h2>
              <ul className="mt-3 grid grid-cols-2 gap-2 text-sm text-ink-200 sm:grid-cols-3">
                {r.amenities.map((a) => (
                  <li key={a.id} className="flex items-center gap-2">
                    <span className="text-pink-400">✓</span>
                    {a.name}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <aside className="h-fit  border border-night-700 bg-night-900 p-5 lg:sticky lg:top-24">
          <p className="text-sm text-ink-400">
            from <span className="text-2xl font-semibold text-ink-50">{money(r.fromRate)}</span> /
            night
          </p>
          <div className="mt-4">
            <StayPicker stay={stay} settings={settings} onChange={update} compact />
          </div>
          <div className="mt-5">
            {!hasDates && <p className="text-sm text-ink-400">Pick your dates to see the total.</p>}
            {quote.isLoading && hasDates && <p className="text-sm text-ink-400">Calculating…</p>}
            {q && q.errors.length > 0 && <Notice tone="warning">{q.errors[0]!.message}</Notice>}
            {q && q.errors.length === 0 && q.soldOut && (
              <Notice tone="danger">Sold out for these dates. Try different nights.</Notice>
            )}
            {q && q.errors.length === 0 && !q.soldOut && <PriceBreakdown price={q} />}
          </div>
          <button
            type="button"
            disabled={!canReserve}
            onClick={() => navigate(`/checkout/${r.slug}${stayQuery(stay)}`)}
            className="btn-primary mt-5 w-full disabled:cursor-not-allowed disabled:opacity-50"
          >
            {q?.soldOut ? 'Sold out' : 'Reserve this room'}
          </button>
          <p className="mt-3 text-center text-xs text-ink-600">
            Paid in full at booking. All sales are final.
          </p>
        </aside>
      </div>
    </Section>
  );
}
